import React, { useState, useEffect } from 'react';
import { ProspectProps } from '@dreamai/types';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../../api';

import { GetProspectResponse } from '@dreamai/types';
import { ReactComponent as EmailIcon } from '../../../../icons/email.svg';
import { ReactComponent as ContactCardIcon } from '../../../../icons/contact-card.svg';

import { useAgent } from '../../../../contexts/AgentContext';
import { generateColorVariants } from '../../sidebar/threadUtils';
import OrgAvatar from '../../../common/OrgAvatar';
import ProspectEmailsView from './ProspectEmailsView';

import './prospect.scss';

interface ProspectComponentProps extends ProspectProps {
  threadId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
}

// Use GetProspectResponse directly instead of custom interface

const Prospect: React.FC<ProspectComponentProps> = ({
  prospectId,
  threadId,
  updateThreadViewVisualDescription,
}) => {
  const [prospectData, setProspectData] = useState<GetProspectResponse | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { getAccessTokenSilently } = useAuth0();
  const { selectedAgent } = useAgent();

  // Generate color variants from agent's predominant color
  const colorVariants = selectedAgent?.predominantColour
    ? generateColorVariants(selectedAgent.predominantColour)
    : {
        base: '#ECBD2D',
        midDarker: '#D4A91A',
        darker: '#B8950F',
        lighter: '#FDF8E8',
      };

  useEffect(() => {
    const fetchProspectData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get Auth0 token and set authorization header
        const token = await getAccessTokenSilently();
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.get<GetProspectResponse>(
          `/prospect/${prospectId}`,
        );
        const data = response.data;

        setProspectData(data);

        // Update visual description for the thread if callback is provided
        if (updateThreadViewVisualDescription) {
          const description = `Viewing prospect: ${data.name} (${data.email}) from ${data.orgName}`;
          await updateThreadViewVisualDescription(description);
        }
      } catch (err: any) {
        let errorMessage = 'Unknown error occurred';

        if (err.response) {
          // API responded with error status
          if (err.response.status === 404) {
            errorMessage =
              'Prospect not found or not authorized to access this prospect';
          } else if (err.response.status === 401) {
            errorMessage = 'Unauthorized access';
          } else {
            errorMessage = `Failed to fetch prospect data: ${err.response.status}`;
          }
        } else if (err instanceof Error) {
          errorMessage = err.message;
        }

        setError(errorMessage);
        console.error('Error fetching prospect data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (prospectId) {
      fetchProspectData();
    } else {
      setError('No prospect ID provided');
      setLoading(false);
    }
  }, [prospectId, updateThreadViewVisualDescription, getAccessTokenSilently]);

  // Loading state
  if (loading) {
    return (
      <div className='prospect-component loading'>
        <div className='d-flex align-items-center'>
          <div className='spinner-border spinner-border-sm me-2' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
          <span>Loading prospect data...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className='prospect-component error'>
        <div className='alert alert-danger' role='alert'>
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  // Success state with proper prospect data display
  if (prospectData) {
    return (
      <div className='prospect-outer-wrapper'>
        <div className='prospect-header'>
          <div className='image-line'>
            <OrgAvatar
              imageUrl={prospectData.orgImageUrl}
              orgName={prospectData.orgName}
            />
            <span className='org-name'>{prospectData.orgName}</span>
          </div>
          <h4>{prospectData.name}</h4>
          {!prospectData.isGeneric ? (
            <span className='job-title'>{prospectData.jobTitle || ''}</span>
          ) : (
            <span className='job-title'>{prospectData.email}</span>
          )}
        </div>

        <div
          className='prospect-big-description'
          style={{
            border: `1px solid ${colorVariants.base}`,
            background: `linear-gradient(90deg, ${colorVariants.lighter} 0%, ${colorVariants.base} 100%)`,
          }}
        >
          {prospectData.orgDescription && (
            <div className='org-description'>
              <strong>About {prospectData.orgName}:</strong>
              <p>{prospectData.orgDescription}</p>
            </div>
          )}
          {prospectData.prospectReasoning && (
            <div className='prospect-reasoning'>
              <strong>
                Why{' '}
                {prospectData.isGeneric
                  ? prospectData.email
                  : prospectData.name}
                :
              </strong>
              <p>{prospectData.prospectReasoning}</p>
            </div>
          )}
        </div>

        <div className='prospect-details'>
          <h6>Prospect Details</h6>
          <div className='prospect-details-inner-wrapper'>
            <div className='prospect-details-email'>
              <EmailIcon />
              <span>{prospectData.email}</span>
            </div>
            {(prospectData.linkedinUrl || prospectData.orgDomain) && (
              <div
                className={`prospect-details-linkedin ${!prospectData.linkedinUrl && !prospectData.orgDomain ? 'inactive' : ''}`}
              >
                <ContactCardIcon />
                {prospectData.linkedinUrl ? (
                  <a
                    href={prospectData.linkedinUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Show LinkedIn profile
                  </a>
                ) : prospectData.orgDomain ? (
                  <a
                    href={`https://${prospectData.orgDomain}`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {prospectData.orgDomain}
                  </a>
                ) : (
                  <span>No website found</span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className='prospect-emails'>
          <h6>Campaign messages</h6>
          <ProspectEmailsView
            prospectId={prospectData.id}
            threadId={threadId}
            updateThreadViewVisualDescription={
              updateThreadViewVisualDescription
            }
          />
        </div>
      </div>
    );
  }

  return null;
};

export default Prospect;
